// ============================================================================
// REFACTORED PLACE ORDER MODAL - MAIN COMPONENT
// ============================================================================

import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogTitle,
  Grid,
  Slide,
  IconButton,
  Tooltip,
} from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import { alpha, styled } from "@mui/material/styles";
import { TransitionProps } from "@mui/material/transitions";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import ErrorHandler from "../../../utils/errorHandler";
import { LOADING_KEYS, useLoadingStore } from "../../../utils/loadingManager";
import OrderDetails from "../../orders/OrderForm/OrderDetails";
import { ProductSection } from "../../orders/OrderForm/ProductSection";
import { EnhancedOrderForm } from "../../orders/enhanced/components/EnhancedOrderForm";
import {
  addOrder,
  fetchOrders,
  selectSelectedOrder,
  updateOrderById,
} from "../../orders/orderSlice";

// Import extracted modules
import {
  ClipboardDocumentCheckIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { SiteDetailsSection, StatusMessages } from "./components";
import {
  useOrderCreationState,
  useOrderDetailsManagement,
  useProductManagement,
  useSiteManagement,
} from "./hooks";
import {
  transformToApiPayload,
  transformToReduxOrderData,
} from "./transformers";
import { PlaceOrderModalProps } from "./types";
import { validateOrderPayload } from "./utils";
import { useParams, useNavigate } from "react-router-dom";
import { fetchSites } from "../siteSlice";
import { useSidePanel } from "../../../hooks/useSidePanel";
import { ProductSidePanel, ItemStageSidePanel } from "../../../components/common";
import type { ItemStageSidePanelRef } from "../../../components/common";

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const StyledPaper = styled(Box)(({ theme }) => ({
  padding: "16px",
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(8px)",
  borderRadius: "16px",
  width: "100%",
}));

// ============================================================================
// TRANSITION COMPONENT
// ============================================================================

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PlaceOrderModal: React.FC<PlaceOrderModalProps> = ({
  isOpen = true,
  onClose,
  onOrderCreated,
  onOrderUpdated,
  // siteId,
  // customerId,
  editMode,
  existingOrder,
}) => {
  const dispatch = useAppDispatch();
  const { loading: orderLoading } = useSelector(
    (state: RootState) => state?.orders || { loading: false }
  );
  const { setLoading, getLoading } = useLoadingStore();
  const orderCreationLoading = getLoading(LOADING_KEYS.ORDER_CREATE);
  const selectedOrder = useSelector(selectSelectedOrder);

  const { siteId = "", customerId = "" } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    console.log("=======siteId==========", siteId);
    console.log("=======customerId==========", customerId);
  }, []);
  useEffect(() => {
    // Fetch sites based on available parameters
    if (customerId && siteId) {
      // Both customerId and siteId available
      dispatch(fetchSites({ customerId, siteId }));
    } else if (customerId && !siteId) {
      // Only customerId available
      dispatch(fetchSites({ customerId }));
    } else if (!customerId && siteId) {
      // Only siteId available
      dispatch(fetchSites({ siteId }));
    } else if (
      editMode &&
      existingOrder?.customer_id &&
      existingOrder?.site_id
    ) {
      // Edit mode with existing order data
      dispatch(
        fetchSites({
          customerId: existingOrder.customer_id,
          siteId: existingOrder.site_id,
        })
      );
    }
  }, [customerId, siteId, editMode, existingOrder, dispatch]);
  // Custom hooks for state management

  const {
    sites,
    selectedSiteId,
    selectedCustomerId,
    siteSearch,
    isEditingSite,
    siteData,
    setSelectedSiteId,
    setSelectedCustomerId,
    setSiteSearch,
    setIsEditingSite,
    setSiteData,
  } = useSiteManagement(siteId, customerId, editMode, existingOrder, isOpen);

  const {
    productData,
    products,
    setProductData,
    setProducts,
    handleProductDataUpdate,
    handleProductSectionSubmit,
  } = useProductManagement(editMode, existingOrder, isOpen);

  const { orderDetailData, setOrderDetailData, handleOrderDetailsUpdate } =
    useOrderDetailsManagement(editMode, existingOrder, siteData);

  const { orderCreationState, setOrderCreationState } = useOrderCreationState();

  // Side panel management for viewing products and items
  const {
    showItemSidePanel,
    showProductSidePanel,
    showItemStages,
    selectedProductForItems,
    selectedItemForStages,
    openProductPanel,
    openItemSidePanelFromOrder,
    openItemStagesPanel,
    closeProductPanel,
    closeItemPanel,
    closeItemStagesPanel,
    closeAll,
    backToProductsFromOrderItems,
  } = useSidePanel();

  const sidePanelRef = React.useRef<ItemStageSidePanelRef>(null);

  // Handle product updates from item configuration panel
  const handleProductUpdate = (updatedProduct: any) => {
    console.log('🔄 PlaceOrderModal - Product updated:', updatedProduct);
    
    // Update the products array with the updated product
    setProducts(prevProducts => {
      const updatedProducts = prevProducts.map(p => 
        p.id === updatedProduct.id ? updatedProduct : p
      );
      console.log('🔄 PlaceOrderModal - Updated products array:', updatedProducts);
      return updatedProducts;
    });
    
    // Also update the single product data if it matches
    if (productData.id === updatedProduct.id) {
      setProductData(updatedProduct);
    }
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleSubmit = async (e: React.FormEvent) => {
    
    
    console.trace(" 🚨 handleSubmit stack");
    console.log("????????submit?????????????????");
    e.preventDefault();
    console.log("====================>siteData:", siteData);
    console.log("======================>selectedSiteId", selectedSiteId);
    console.log(
      "======================>selectedCustomerId:",
      selectedCustomerId
    );

    let requestPayload = {
      customerId,
      siteId,
      products: products,
      orderDetailData: orderDetailData,
    };
    console.log(
      "\n\n ===== Request Payload Begins ===== \n\n",
      JSON.stringify(requestPayload),
      "\n\n ===== Request Payload Ends ===== \n\n"
    );

    // Set loading state
    setLoading(LOADING_KEYS.ORDER_CREATE, true, "Creating order...");

    try {
      // Validate siteData first
      if (!selectedSiteId || !selectedCustomerId) {
        throw new Error("Invalid site data. Please select a valid site.");
      }

      // Ensure we have products to send
      const productsToSend = products.length > 0 ? products : [productData];
      console.log("Products to send:", productsToSend);

      // Transform to API payload
      const apiPayload = transformToApiPayload(productsToSend, orderDetailData);
      console.log("Transformed API payload:", apiPayload);
      apiPayload.siteId = selectedSiteId;
      apiPayload.customerId = selectedCustomerId;
      console.log("API payload siteId:", apiPayload.siteId);
      console.log("API payload customerId:", apiPayload.customerId);

      // Validate payload
      const validationErrors = validateOrderPayload(apiPayload);
      if (validationErrors.length > 0) {
        console.error("Validation errors:", validationErrors);
        ErrorHandler.handleValidationError(validationErrors, "Order creation");
        setLoading(LOADING_KEYS.ORDER_CREATE, false);
        return;
      }

      // Transform to Redux order format
      // const orderData = transformToReduxOrderData(apiPayload);
  
      // Dispatch the appropriate action based on mode
      let result;
      if (editMode && existingOrder) {
        // Update existing order
        console.log(
          "-------Updating existing order with ID:---------",
          existingOrder.id
        );
        console.log("-------Order data for update:-------", apiPayload);
        result = await dispatch(
          updateOrderById({
            orderId: existingOrder.id,
            orderData: apiPayload as any,
          })
        );
        if (onOrderCreated) onOrderCreated(result.payload);
      } else {
        console.log("-------Order data for create:-------...................", apiPayload);
        // Create new order
        result = await dispatch(addOrder(apiPayload as any));
        if (onOrderCreated) onOrderCreated(result.payload);
      }

      if (
        updateOrderById.fulfilled.match(result) ||
        addOrder.fulfilled.match(result)
      ) {
        await dispatch(fetchOrders({}));
      }

      // Check if the action was fulfilled or rejected
      if (
        (editMode ? updateOrderById.fulfilled : addOrder.fulfilled).match(
          result
        )
      ) {
        // Success case
        const successMessage = editMode
          ? "Order updated successfully!"
          : "Order created successfully!";
        ErrorHandler.handleApiSuccess(successMessage);
        setOrderCreationState({
          isLoading: false,
          error: null,
          success: successMessage,
        });

        // Call appropriate callback or navigate
        if (editMode && onOrderUpdated) {
          onOrderUpdated(result.payload);
        } else if (!editMode && onOrderCreated) {
          onOrderCreated(result.payload);
        } else {
          // If no callback provided, navigate to orders page
          navigate('/orders');
        }

        // onClose(); // Close modal on success
      } else if (
        (editMode ? updateOrderById.rejected : addOrder.rejected).match(result)
      ) {
        // Error case - handle the error
        const error = result.payload || result.error;
        ErrorHandler.handleApiError(error, "Order creation");
        setOrderCreationState({
          isLoading: false,
          error:
            typeof error === "string"
              ? error
              : (error as any)?.message || "Failed to create order",
          success: null,
        });
      }
    } catch (error) {
      console.error("Error creating order:", error);
      ErrorHandler.handleApiError(error, "Order creation");
      setOrderCreationState({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Failed to create order",
        success: null,
      });
    } finally {
      // Always clear loading state
      setLoading(LOADING_KEYS.ORDER_CREATE, false);
    }
  };

  const handleSiteSelection = (site: any) => {
    console.log("Site selected:", site);
    setSelectedSiteId(site ? site.id : "");
    setSelectedCustomerId(site ? site.customer.customerId : "");
    setSiteData(site); // Set the siteData when user selects a site
    setIsEditingSite(false);
  };

  const handleEditSite = () => {
    setSelectedSiteId("");
    setSelectedCustomerId("");
    setSiteSearch("");
    setSiteData(null); // Clear siteData to show dropdown
    setIsEditingSite(true);
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    // console.log("Site Data in PlaceOrderModal:", siteData);
    // console.log("Selected Order Data in PlaceOrderModal:", selectedOrder);
  }, [siteData, selectedOrder]);

  useEffect(() => {
    // console.log("Updated product data received from child:", productData);
    if (productData && productData.id) {
      setProducts([productData]);
    }
  }, [productData]);

  useEffect(() => {
    console.log("======================>CustomerId:", customerId);
    console.log(
      "======================>selectedCustomerId:",
      selectedCustomerId
    );
  }, [customerId, selectedCustomerId]);

  // useEffect(() => {
  //   console.log("Products state updated:", products);
  //   console.log("Products length:", products.length);
  //   if (products.length > 0) {
  //     console.log("First product details:", products[0]);
  //     console.log("First product frame items:", products[0].frame);
  //     console.log("First product panel items:", products[0].panel);
  //   }
  // }, [products]);

  // useEffect(() => {
  //   console.log("OrderDetailData updated:", orderDetailData);
  // }, [orderDetailData]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Grid
      container
      spacing={2}
      sx={{
        flex: 1,
        // px: 0,
         backgroundColor: "red",
        alignItems: "center",
        mb:-2
      }}
    >
      <Box sx={{ display: "flex", flex: 1,  }}>
        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            height: "100%",
            
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            // px={2}
            mb={2}
            // pt={2}
            sx={{ flexShrink: 0 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DialogTitle sx={{ p: 0 }}>
                {editMode ? "Edit Order" : "Place New Order"}
              </DialogTitle>
              {editMode && existingOrder && (
                <Tooltip title="View product items and stages">
                  <IconButton
                    size="small"
                    onClick={() => {
                      // Open product panel to show all products, user can then select one to see items
                      if (existingOrder.products && existingOrder.products.length > 0) {
                        openProductPanel(existingOrder, existingOrder.products);
                      }
                    }}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'primary.dark',
                      },
                    }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {/* <Button
              type="submit"
              variant="contained"
              disabled={orderCreationLoading.isLoading}
              // onClick={onSubmit}
              sx={{
                minWidth: 160,
                height: 44,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                backgroundColor: editMode ? "warning.main" : "primary.main",
                "&:hover": {
                  backgroundColor: editMode ? "warning.dark" : "primary.dark",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                },
                "&:disabled": {
                  opacity: 0.6,
                  cursor: "not-allowed",
                },
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              startIcon={
                orderCreationLoading.isLoading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : editMode ? (
                  <PencilIcon className="h-4 w-4" />
                ) : (
                  <ClipboardDocumentCheckIcon className="h-4 w-4" />
                )
              }
            >
              {orderCreationLoading.isLoading
                ? orderCreationLoading.operation ||
                  (editMode ? "Updating..." : "Creating...")
                : editMode
                ? "Update Order"
                : "Create Order"}
            </Button> */}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
              flex: 1,
              mb: 3,
              alignItems: "stretch",
            }}
          >
            {/* Site Details Section - 6 columns */}
            <Box>
              <SiteDetailsSection
                siteData={siteData}
                sites={sites}
                selectedCustomerId={customerId}
                selectedSiteId={siteId}
                siteSearch={siteSearch}
                isEditingSite={isEditingSite}
                editMode={editMode}
                onSiteSelection={handleSiteSelection}
                onSiteSearchChange={setSiteSearch}
                onEditSite={handleEditSite}
              />
            </Box>

            {/* Order Details Section - 6 columns */}
            <Box>
              <OrderDetails
                data={orderDetailData as any}
                selectedOrder={editMode ? existingOrder : selectedOrder}
                setData={handleOrderDetailsUpdate}
              />
            </Box>
          </Box>

          {/* Scrollable Content Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              paddingBottom: 2,
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(0,0,0,0.2)",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(0,0,0,0.3)",
              },
            }}
          >
            {/* Status Messages */}
            <StatusMessages orderCreationState={orderCreationState} />

            {/* Enhanced Product Section with Item Configuration Panel */}
            <EnhancedOrderForm
              selectedCustomer={selectedCustomerId}
              selectedSite={selectedSiteId}
              customers={[]} // Will be populated by the component
              sites={sites}
              onCustomerChange={() => {}} // Not needed for edit mode
              onSiteChange={() => {}} // Not needed for edit mode
              configuredProducts={products}
              onProductUpdate={handleProductUpdate}
              editMode={editMode}
              existingOrder={editMode ? existingOrder : null}
            />
          </Box>
          
          <Box
            sx={{
              // bg-white dark:bg-gray-900 rounded-2xl shadow-md p-6
              position: "sticky",
              bottom: 0,
              zIndex: 1000,
              backgroundColor: "white",
              // borderRadius: 2,               
              // boxShadow: 3,    
              mb: -2,
              pb: -2
            }}
          >
          <DialogActions sx={{ px: 3, py: 2,  }}>
          
            <Button
              type="submit"
              variant="contained"
              disabled={orderCreationLoading.isLoading}
              // onClick={onSubmit}
              sx={{
                minWidth: 160,
                height: 44,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                backgroundColor: editMode ? "warning.main" : "primary.main",
                "&:hover": {
                  backgroundColor: editMode ? "warning.dark" : "primary.dark",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                },
                "&:disabled": {
                  opacity: 0.6,
                  cursor: "not-allowed",
                },
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              }}
              startIcon={
                orderCreationLoading.isLoading ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : editMode ? (
                  <PencilIcon className="h-4 w-4" />
                ) : (
                  <ClipboardDocumentCheckIcon className="h-4 w-4" />
                )
              }
            >
              {orderCreationLoading.isLoading
                ? orderCreationLoading.operation ||
                  (editMode ? "Updating..." : "Creating...")
                : editMode
                ? "Update Order"
                : "Create Order"}
            </Button> 
          
          </DialogActions>
          </Box>
        </form>
      </Box>

      {/* Product Side Panel - Shows all products from the order */}
      {editMode && existingOrder && (
        <ProductSidePanel
          isOpen={showProductSidePanel}
          onClose={closeProductPanel}
          onCloseAll={closeAll}
          products={existingOrder.products || []}
          onProductSelect={(product) => {
            openItemSidePanelFromOrder(product);
          }}
          title={existingOrder?.orderNumber || existingOrder?.order_tracking_code || existingOrder?.id ? `Order ${existingOrder.orderNumber || existingOrder.order_tracking_code || existingOrder.id} - Sets` : undefined}
          selectedProductId={selectedProductForItems?.id}
        />
      )}

      {/* Item & Stage Side Panel - Shows items and stages for selected product */}
      {editMode && existingOrder && (
        <ItemStageSidePanel
          ref={sidePanelRef}
          isOpen={showItemSidePanel && !!selectedProductForItems}
          onClose={closeItemPanel}
          onCloseAll={closeAll}
          product={selectedProductForItems}
          onItemSelect={(item) => {
            openItemStagesPanel(item);
          }}
          onStageSelect={(item, stage) => {
            console.log('Stage selected:', item, stage);
          }}
          title={selectedProductForItems ? `${selectedProductForItems.type ? selectedProductForItems.type.charAt(0).toUpperCase() + selectedProductForItems.type.slice(1) : 'Product'} - Parts` : undefined}
          order={existingOrder}
        />
      )}
    </Grid>
  );
};

export default PlaceOrderModal;
